import {
  edge,
  isManager,
  query,
  requireClient,
  rpc,
} from "../../lib/supabase.js";
export async function listPayments(month, keyword, tab = "all") {
  const rows = [];
  let cursor = null;
  let counts;
  do {
    const page = await rpc("get_monthly_payment_overview", {
      target_billing_month: `${month}-01`,
      status_filter: tab,
      search_text: keyword.trim() || null,
      page_size: 100,
      ...(cursor
        ? {
            after_student_name: cursor.studentName,
            after_student_id: cursor.studentId,
          }
        : {}),
    });
    rows.push(...page.rows);
    counts = page.counts;
    cursor = page.page.hasMore ? page.page.nextCursor : null;
  } while (cursor);
  return { rows, counts };
}
export const previewPayments = (month, ids) =>
  rpc("preview_monthly_invoice_items", {
    target_billing_month: `${month}-01`,
    target_student_ids: ids,
  });
export const paymentDetail = (month, id) =>
  rpc("get_monthly_payment_detail", {
    target_student_id: id,
    target_billing_month: `${month}-01`,
  });
export function requireBilling(context) {
  if (!isManager(context.role))
    throw new Error("이 작업에 대한 권한이 없습니다.");
}
export async function sendPayments(context, month, composer) {
  requireBilling(context);
  const outcomes = [];
  // The preparation UUID and returned invoice ID survive retries within this composer.
  for (const row of composer.billRows) {
    try {
      if (!row.invoiceId) {
        const prepared = await rpc("prepare_monthly_invoice_with_recipient", {
          target_student_id: row.key,
          target_billing_month: `${month}-01`,
          target_monthly_charge_item_ids: row.billingItems
            .filter((item) => item.selected && item.billable)
            .map((item) => item.id),
          operation_id: row.operationId,
          requested_product_name: composer.productName,
          requested_payment_starts_on: composer.paymentStartDate,
          requested_payment_due_on: composer.paymentDueDate,
          requested_customer_message: resolveMessage(
            composer.messageTemplate,
            row,
            month,
          ),
          expected_recipient_name: row.recipientName,
        });
        row.invoiceId = prepared.invoiceId;
      }
      const response = await edge("payssam-bill-send", {
        invoiceIds: [row.invoiceId],
      });
      outcomes.push({ studentId: row.key, ...response.results[0] });
    } catch (error) {
      outcomes.push({
        studentId: row.key,
        status: "failed",
        message: error.message,
      });
    }
  }
  return outcomes;
}
export async function recordOnsite(context, month, target, form) {
  requireBilling(context);
  return rpc("record_on_site_payment", {
    target_student_id: target.student.id,
    target_billing_month: `${month}-01`,
    target_monthly_charge_item_ids: target.items
      .filter((item) => item.selected && item.billable)
      .map((item) => item.id),
    payment_amount: form.amount,
    method: form.method,
    occurred_at: `${form.paidAt}:00+09:00`,
    approval_code: form.approvalNumber || null,
    payment_memo: form.memo || null,
    operation_id: target.operationId,
  });
}
export async function billAction(context, invoiceId, action) {
  requireBilling(context);
  const invoice = await query(
    requireClient()
      .from("invoices")
      .select("bill_id")
      .eq("id", invoiceId)
      .eq("academy_id", context.academyId)
      .single(),
  );
  if (!invoice.bill_id)
    throw new Error(
      "청구서 식별 정보를 확인할 수 없습니다. 목록을 새로고침해 주세요.",
    );
  // Bill ID stays in function memory only; no Provider states or payload are stored in UI state.
  await edge(
    action === "destroy" ? "payssam-bill-destroy" : "payssam-bill-sync",
    { invoiceId, billId: invoice.bill_id },
  );
}
export async function cancelPayment(context, target, reason, operationId) {
  requireBilling(context);
  await edge("payssam-payment-cancel", {
    paymentId: target.paymentTransactionId,
    reason,
    operationId,
  });
}

export function resolveMessage(template, row, month) {
  const replacements = {
    "{청구월}": Number(month.slice(5)) + "월",
    "{학생명}": row.studentName,
    "{보호자명}": row.recipientName,
    "{수강수업}": [
      ...new Set(
        row.billingItems
          .filter((item) => item.selected)
          .map((item) => item.title.replace(/^\d{4}-\d{2} /, "")),
      ),
    ].join(", "),
  };
  return Object.entries(replacements).reduce(
    (value, [token, replacement]) => value.split(token).join(replacement),
    template || "",
  );
}
